import { MutableRefObject, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { keyBy, mapValues, omit } from "lodash";
import useNetworkHeaders from "../../../../hooks/useNetworkHeaders";
import { LayerFieldDefinition, updateMapLayer } from "../../queries/maps";
import Graphic from "@arcgis/core/Graphic";
import _ from "lodash";
import { LocationFieldType } from "../../../../constants/LocationFilterTypes";
import { useMapLayer } from "../useMapLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";

enum FieldDefOperationType {
  Add = "add",
  Edit = "edit",
  Delete = "delete",
  SetPrimary = "set_primary",
}
type FieldDefinitionOperation =
  | { type: FieldDefOperationType.Add; field: Omit<LayerFieldDefinition, "layerId"> }
  | { type: FieldDefOperationType.Edit; id: number; newLabel: string }
  | { type: FieldDefOperationType.Delete; id: number }
  | { type: FieldDefOperationType.SetPrimary; id: number };

export function useLayerFieldDefinitions({
  layerId,
  editLayerRef,
}: {
  layerId?: number | string;
  editLayerRef: MutableRefObject<GraphicsLayer | null>;
}) {
  const [pendingOperations, setPendingOperations] = useState<FieldDefinitionOperation[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const networkHeaders = useNetworkHeaders();
  const { layerQuery } = useMapLayer(layerId);

  // This must be memoized or it causes infinite re-renders
  const originalFieldDefinitions = useMemo(
    () => layerQuery.data?.fieldDefinitions || [],
    [layerQuery.data?.fieldDefinitions],
  );

  // Calculate the current field definitions based on original + pending operations
  const fieldDefinitions = useMemo(() => {
    let result = [...originalFieldDefinitions];
    pendingOperations.forEach((op) => {
      switch (op.type) {
        case FieldDefOperationType.Add:
          result.push({
            ...op.field,
            layerId: layerId ? +layerId : 0,
          });
          break;
        case FieldDefOperationType.Edit:
          const index = result.findIndex((fd) => fd.customField.id === op.id);
          if (index >= 0) {
            result[index] = {
              ...result[index],
              customField: {
                ...result[index].customField,
                label: op.newLabel,
              },
            };
          }
          break;
        case FieldDefOperationType.Delete:
          result = result.filter((fd) => fd.customField.id !== op.id);
          break;
        case FieldDefOperationType.SetPrimary:
          result = result.map((fd) => ({
            ...fd,
            isPrimary: fd.customField.id === op.id,
          }));
          break;
      }
    });

    return result;
  }, [originalFieldDefinitions, pendingOperations, layerId]);

  // Explicitly provide a setter function for fieldDefinitions
  // This will be used by the history hook to directly set field definitions
  const setFieldDefinitions = (
    newDefinitions: LayerFieldDefinition[] | ((prev: LayerFieldDefinition[]) => LayerFieldDefinition[]),
  ) => {
    // When setting field definitions directly, calculate the operations needed
    // to transform from the original state to the new state
    let newFieldDefs: LayerFieldDefinition[];

    if (typeof newDefinitions === "function") {
      newFieldDefs = newDefinitions(fieldDefinitions);
    } else {
      newFieldDefs = newDefinitions;
    }

    // Reset pending operations and calculate new ones
    const newOperations: FieldDefinitionOperation[] = [];

    // Find deleted fields
    originalFieldDefinitions.forEach((origField) => {
      const exists = newFieldDefs.some((newField) => newField.customField.id === origField.customField.id);

      if (!exists) {
        newOperations.push({
          type: FieldDefOperationType.Delete,
          id: origField.customField.id,
        });
      }
    });

    // Find added and updated fields
    newFieldDefs.forEach((newField) => {
      const origField = originalFieldDefinitions.find((f) => f.customField.id === newField.customField.id);

      if (!origField) {
        // Field was added
        newOperations.push({
          type: FieldDefOperationType.Add,
          field: {
            customField: newField.customField,
            isPrimary: newField.isPrimary,
          },
        });
      } else if (origField.customField.label !== newField.customField.label) {
        // Label was updated
        newOperations.push({
          type: FieldDefOperationType.Edit,
          id: newField.customField.id,
          newLabel: newField.customField.label,
        });
      }

      // Check for primary state change
      if (origField && origField.isPrimary !== newField.isPrimary && newField.isPrimary) {
        newOperations.push({
          type: FieldDefOperationType.SetPrimary,
          id: newField.customField.id,
        });
      }
    });

    setPendingOperations(newOperations);
    setHasUnsavedChanges(newOperations.length > 0);
  };

  const [updateLayer] = useMutation(updateMapLayer(networkHeaders), {
    onSuccess: () => {
      if (layerId) layerQuery.refetch();
    },
  });

  const handleAddColumn = (newColumnName: string, isPrimary?: boolean) => {
    const fieldName = newColumnName.toLowerCase().replace(/[^a-zA-Z0-9_]/g, "_");
    const existingField = fieldDefinitions.find((fd) => fd.customField.name === fieldName);
    let uniqueFieldName = fieldName;
    if (existingField) {
      let counter = 1;
      while (fieldDefinitions.some((fd) => fd.customField.name === `${fieldName}_${counter}`)) {
        counter++;
      }
      uniqueFieldName = `${fieldName}_${counter}`;
    }

    const newCustomField = {
      // Generate a random negative ID for new fields so we can track using id.
      // Tracking by name is bad since if they were to ever change, we'd have
      // to iterate through every feature to replace the attribute name
      id: -parseInt(crypto.randomUUID().replace(/-/g, "").substring(0, 10), 16),
      label: newColumnName,
      name: uniqueFieldName,
      type: "Text" as LocationFieldType,
      description: `${newColumnName} field`,
      required: false,
    };

    const newField = {
      layerId: layerId ? +layerId : 0,
      customField: newCustomField,
      // Make the field primary if it's the first field added
      isPrimary: isPrimary ?? !fieldDefinitions.length,
    };

    setPendingOperations((prev) => [...prev, { type: FieldDefOperationType.Add, field: newField }]);
    setHasUnsavedChanges(true);
    return newField;
  };

  const handleUpdateColumnLabel = (fieldId: number, newLabel: string) => {
    if (!newLabel.trim()) return;

    setPendingOperations((prev) => [
      ...prev,
      {
        type: FieldDefOperationType.Edit,
        id: fieldId,
        newLabel: newLabel,
      },
    ]);

    setHasUnsavedChanges(true);
  };

  const handleDeleteColumn = (fieldId: number) => {
    // Don't allow deleting the primary field
    const primaryFieldId = fieldDefinitions.find((fd) => fd.isPrimary)?.customField.id;
    if (fieldId === primaryFieldId) return;

    setPendingOperations((prev) => [...prev, { type: FieldDefOperationType.Delete, id: fieldId }]);
    setHasUnsavedChanges(true);
  };

  const handleSetPrimaryColumn = (fieldId: number) => {
    setPendingOperations((prev) => [...prev, { type: FieldDefOperationType.SetPrimary, id: fieldId }]);
    setHasUnsavedChanges(true);
  };

  const saveFieldDefinitions = async (newLayerId?: string | number) => {
    if ((!layerId && !newLayerId) || !hasUnsavedChanges) return layerQuery.data;

    try {
      const newCustomFields = fieldDefinitions.map((fd) => ({
        ...fd.customField,
        isPrimary: fd.isPrimary || false,
      }));
      const fields = newCustomFields.map((fd) => ({
        ...fd,
        id: fd.id < 0 ? undefined : fd.id,
      }));

      const result = await updateLayer({
        id: +(layerId ?? newLayerId!),
        data: {
          fields,
        },
      });

      // Clear pending operations after successful save
      setPendingOperations([]);
      setHasUnsavedChanges(false);

      if (!result?.[0].fieldDefinitions) return result;

      // When the query gets refetched, it won't have any of the new field defs.
      // So we need to set the field definitions to the new ones manually
      setFieldDefinitions(result[0].fieldDefinitions);

      // Then edit all the graphics so that their field def ids are updated. Before they were the temporary ids.
      const nameToNewId = keyBy(result[0].fieldDefinitions, (fd) => fd.customField.name);
      const oldIds = keyBy(fieldDefinitions, (fd) => fd.customField.id);
      const idMap = mapValues(oldIds, (fd) => nameToNewId[fd.customField.name]?.customField.id);
      const graphics = editLayerRef.current?.graphics.toArray() || [];
      graphics.forEach((graphic) => {
        const attributes = graphic.attributes;
        if (!attributes) return;
        Object.keys(attributes).forEach((key) => {
          if (!idMap[key]) return;
          attributes[idMap[key]] = attributes[key];
          if (+key !== idMap[key]) delete attributes[key];
        });
      });
      return result;
    } catch (error) {
      console.error("Error saving field definitions:", error);
      throw error;
    }
  };

  const discardFieldChanges = () => {
    setPendingOperations([]);
    setHasUnsavedChanges(false);
  };

  const prepareFieldsForSubmission = (
    graphic: Graphic,
    oldFieldDefs: LayerFieldDefinition[],
    newFieldDefs: LayerFieldDefinition[],
  ) => {
    // Exclude tracking metadata
    const fields = omit(graphic.attributes ?? {}, "graphicId", "featureId");

    // Map old field definition ids (in memory ids) to the new field def ids that we get back from server
    const newIds = mapValues(
      keyBy(oldFieldDefs, (fd) => fd.customField.id),
      (fd) => newFieldDefs?.find((nfd) => nfd.customField.name === fd.customField.name)?.customField.id,
    );

    // Map field names to their IDs into {id, value}[]
    const values = _.map(fields, (value, id) => ({
      id: newIds[id] || +id,
      value,
    }));
    return values.filter((field) => field.id && field.id > 0);
  };

  return {
    fieldDefinitions,
    setFieldDefinitions,
    layerQuery,
    handleAddColumn,
    handleDeleteColumn,
    handleUpdateColumnLabel,
    handleSetPrimaryColumn,
    prepareFieldsForSubmission,
    saveFieldDefinitions,
    hasUnsavedChanges,
    discardFieldChanges,
  };
}
